/**
 * Admin Resource
 *
 * Thin 1:1 mapping to the admin API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  AuditLog,
  ListAdminActionsParams,
  GetEntityAuditTrailParams,
  UsageSummary,
  GetPlatformUsageParams,
  GetAccountUsageParams,
  ExportUsageParams,
  AdminSession,
  ListSessionsAdminParams,
  ReplaySessionRequest,
  ReplaySessionResponse,
  ActiveAgent,
  ListActiveAgentsParams,
  TerminateAgentResponse,
  BridgeConnection,
  SystemPersonaWithConfig,
  ModelRoleOverridesList,
  OverridableModelRoleName,
  PutModelRoleOverrideRequest,
  ModelRoleOverride,
  DeleteModelRoleOverrideResponse,
  OrganizationModelRoleOverride,
  DeleteOrganizationModelRoleOverrideResponse,
} from '../types/index.js';

export class AdminResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/admin';

  // --- Audit Logs ---

  async listAdminActions(
    params?: ListAdminActionsParams,
  ): Promise<AuditLog[]> {
    return this.http.get(`${this.prefix}/audit/logs`, params);
  }

  async getEntityAuditTrail(
    entityType: string,
    entityId: string,
    params?: GetEntityAuditTrailParams,
  ): Promise<AuditLog[]> {
    return this.http.get(
      `${this.prefix}/audit/entity/${entityType}/${entityId}`,
      params,
    );
  }

  // --- Usage Analytics ---

  async getPlatformUsageSummary(
    params?: GetPlatformUsageParams,
  ): Promise<UsageSummary> {
    return this.http.get(`${this.prefix}/usage/summary`, params);
  }

  async getAccountUsageDetails(
    accountId: string,
    params: GetAccountUsageParams,
  ): Promise<UsageSummary> {
    return this.http.get(
      `${this.prefix}/usage/account/${accountId}`,
      params,
    );
  }

  async exportUsageData(params: ExportUsageParams): Promise<unknown> {
    return this.http.get(`${this.prefix}/usage/export`, params);
  }

  // --- Sessions Admin ---

  async listSessions(
    params?: ListSessionsAdminParams,
  ): Promise<AdminSession[]> {
    return this.http.get(`${this.prefix}/sessions`, params);
  }

  async getSession(sessionId: string): Promise<AdminSession> {
    return this.http.get(`${this.prefix}/sessions/${sessionId}`);
  }

  async replaySession(
    sessionId: string,
    params: ReplaySessionRequest,
  ): Promise<ReplaySessionResponse> {
    return this.http.post(
      `${this.prefix}/sessions/${sessionId}/replay`,
      params,
    );
  }

  // --- Agents Admin ---

  async listActiveAgents(
    params?: ListActiveAgentsParams,
  ): Promise<ActiveAgent[]> {
    return this.http.get(`${this.prefix}/agents`, params);
  }

  async terminateAgent(
    workflowId: string,
    reason: string,
  ): Promise<TerminateAgentResponse> {
    return this.http.post(
      `${this.prefix}/agents/${workflowId}/terminate?reason=${encodeURIComponent(reason)}`,
    );
  }

  // --- Bridge Admin ---

  async listBridgeConnections(): Promise<BridgeConnection[]> {
    return this.http.get(
      `${this.prefix}/infrastructure/bridge/connections`,
    );
  }

  // --- System Personas ---

  /** Admin catalog, including reserved `_system_*` cognitive personas. */
  async listSystemPersonas(): Promise<SystemPersonaWithConfig[]> {
    return this.http.post(`${this.prefix}/system-personas/list`, {});
  }

  // --- Model Role Overrides ---

  /** Every model role for one account: the stack default, the account override if any, and which one resolves. */
  async listModelRoleOverrides(
    organizationId: string,
    accountId: string,
  ): Promise<ModelRoleOverridesList> {
    return this.http.get(this.modelRoleOverridesPath(organizationId, accountId));
  }

  /** Set one account's override for a model role. The model must be in the catalog, and a `vision_default` override must name a vision-capable model. */
  async putModelRoleOverride(
    organizationId: string,
    accountId: string,
    role: OverridableModelRoleName,
    params: PutModelRoleOverrideRequest,
  ): Promise<ModelRoleOverride> {
    return this.http.put(
      `${this.modelRoleOverridesPath(organizationId, accountId)}/${encodeURIComponent(role)}`,
      params,
    );
  }

  /** Remove one account's override for a model role, so the role resolves to the stack default again. */
  async deleteModelRoleOverride(
    organizationId: string,
    accountId: string,
    role: OverridableModelRoleName,
  ): Promise<DeleteModelRoleOverrideResponse> {
    return this.http.delete(
      `${this.modelRoleOverridesPath(organizationId, accountId)}/${encodeURIComponent(role)}`,
    );
  }

  /** Set a model role override for every account in an organization; accounts created there later inherit it. An account's own override replaces it for that account. */
  async putOrganizationModelRoleOverride(
    organizationId: string,
    role: OverridableModelRoleName,
    params: PutModelRoleOverrideRequest,
  ): Promise<OrganizationModelRoleOverride> {
    return this.http.put(
      `${this.prefix}/organizations/${encodeURIComponent(organizationId)}/model-role-overrides/${encodeURIComponent(role)}`,
      params,
    );
  }

  /** Remove an organization-wide model role override from every account that holds it; account-specific overrides stay. */
  async deleteOrganizationModelRoleOverride(
    organizationId: string,
    role: OverridableModelRoleName,
  ): Promise<DeleteOrganizationModelRoleOverrideResponse> {
    return this.http.delete(
      `${this.prefix}/organizations/${encodeURIComponent(organizationId)}/model-role-overrides/${encodeURIComponent(role)}`,
    );
  }

  private modelRoleOverridesPath(organizationId: string, accountId: string): string {
    return `${this.prefix}/organizations/${encodeURIComponent(organizationId)}/accounts/${encodeURIComponent(accountId)}/model-role-overrides`;
  }
}
